import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { FeedbackType, FeedbackFormData } from '@/types/feedback';
import { useFeedbackForm } from '@/hooks/useFeedbackForm';
import { useUnreadChatCount } from '@/features/chat/hooks';
import type { FabMode } from '@/features/chat/types';
import { FeedbackChatContext, type FeedbackChatContextType } from './useFeedbackChat';

export const FeedbackChatProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {

  // Feedback state
  const [isFeedbackOpen, setIsFeedbackOpen] = useState<boolean>(false);
  const [feedbackType, setFeedbackType] = useState<FeedbackType | undefined>(undefined);

  // Chat state
  const [isChatOpen, setIsChatOpen] = useState<boolean>(false);

  // FAB state - Always default to feedback mode for all users
  const [fabMode, setFabMode] = useState<FabMode>('feedback');

  // Shared state
  const [currentPageSource, setCurrentPageSource] = useState<string>('');

  const location = useLocation();
  const { isSubmitting, submitFeedback: handleSubmitFeedback } = useFeedbackForm();
  const { unreadCount: unreadChatCount } = useUnreadChatCount();

  // Update page source when location changes
  useEffect(() => {
    const path = location.pathname;
    const pageName = path.substring(1) || 'home';
    setCurrentPageSource(pageName);
  }, [location]);

  // Auto-switch to chat mode when there are unread messages
  useEffect(() => {
    if (unreadChatCount > 0 && fabMode === 'feedback') {
      setFabMode('chat');
    }
  }, [unreadChatCount, fabMode]);

  // Feedback functions
  const openFeedback = (type?: FeedbackType) => {

    setFeedbackType(type);
    setIsFeedbackOpen(true);
    // Close chat if open
    setIsChatOpen(false);
  };
  
  const closeFeedback = () => {

    setIsFeedbackOpen(false);
    setFeedbackType(undefined);
  };

  const submitFeedback = async (data: FeedbackFormData) => {
    const updatedData = {
      ...data,
      pageSource: data.pageSource || currentPageSource
    };
    
    const success = await handleSubmitFeedback(updatedData);
    if (success) {
      closeFeedback();
    }
  };

  // Chat functions
  const openChat = () => {

    setIsChatOpen(true);
    // Close feedback if open
    setIsFeedbackOpen(false);
  };

  const closeChat = () => {

    setIsChatOpen(false);
  };

  const contextValue: FeedbackChatContextType = {
    // Feedback
    isFeedbackOpen,
    openFeedback,
    closeFeedback,
    submitFeedback,
    isSubmitting,
    currentPageSource,
    feedbackType,
    
    // Chat
    isChatOpen,
    openChat,
    closeChat,
    
    // FAB
    fabMode,
    setFabMode,
    unreadChatCount,
  };

  return (
    <FeedbackChatContext.Provider value={contextValue}>
      {children}
    </FeedbackChatContext.Provider>
  );
};
